import { useState, useEffect, useMemo, useRef } from 'react'
import { SECTIONS } from '../components/Layout'
import { menuPermisosApi } from '../api/menuPermisos'
import Boton from '../components/Boton'
import PermisosPorPersona from '../components/PermisosPorPersona'
import { useAuth } from '../context/AuthContext'

// Los roles vienen del backend (tabla spatie, ABM /configuracion/roles) — NUNCA
// hardcodear: los roles nuevos tienen que aparecer solos en la matriz (7 jun 2026).
const etiquetaRol = (r) => r.replaceAll('_', ' ').replace(/^./, c => c.toUpperCase())

// Checkbox con soporte de estado intermedio (algunos hijos tildados).
function TriCheck({ checked, indeterminate, disabled, onChange, title }) {
  const ref = useRef(null)
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !checked && !!indeterminate
  }, [checked, indeterminate])
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      title={title}
      style={{ width: 17, height: 17, cursor: disabled ? 'not-allowed' : 'pointer' }}
    />
  )
}

export default function MenuPermisosPage() {
  const { isSuperAdmin, impersonateRole } = useAuth()
  // matriz: { rol: Set(paths) }
  const [matriz, setMatriz] = useState(null)
  const [roles, setRoles] = useState([])           // roles configurables (dinámicos, del backend)
  const [guardando, setGuardando] = useState('')   // rol que se está guardando
  const [error, setError] = useState('')
  const [cerrados, setCerrados] = useState(() => new Set())  // claves de nodos colapsados

  useEffect(() => {
    menuPermisosApi.matriz().then(({ data }) => {
      const rs = data.roles || []
      const m = {}
      rs.forEach(r => { m[r] = new Set(data.matriz?.[r] || []) })
      setRoles(rs)
      setMatriz(m)
    }).catch(() => setError('No se pudo cargar la configuración.'))
  }, [])

  // Árbol configurable: todas las secciones menos las adminOnly.
  //
  // SISTEMA sí entra (04/08/2026): se puede repartir por rol Magento, Autómata,
  // Correos, Log de Actividad, Sugerencias y ERP Presea. Quedan afuera sus
  // cuatro LLAVES (`llave: true` en SECTIONS) — Usuarios, Roles, Menú por rol y
  // Tablas —, que son siempre solo-admin: quien entra ahí puede darse a sí mismo
  // todo lo demás, o leer lo que no le corresponde. Se muestran igual, en gris,
  // para que se entienda que existen y por qué no se tocan; no tener el ítem
  // sería peor que tenerlo bloqueado con su motivo.
  const arbol = useMemo(() => {
    return SECTIONS.filter(s => !s.adminOnly).map(s => {
      const directos = (s.items || [])
        .filter(it => !it.llave)
        .map(it => ({ path: it.path, label: it.label }))
      const llaves = (s.items || []).filter(it => it.llave).map(it => it.label)
      const subs = (s.subsections || []).map(sub => ({
        key: `sub:${sub.id || sub.label}`,
        label: sub.label,
        items: (sub.items || []).map(it => ({ path: it.path, label: it.label })),
      }))
      const allPaths = [
        ...directos.map(d => d.path),
        ...subs.flatMap(sub => sub.items.map(i => i.path)),
      ]
      return { key: `sec:${s.id || s.label}`, label: s.label, icon: s.icon, directos, subs, allPaths, llaves }
    })
  }, [])

  const todasLasClaves = useMemo(() => {
    const ks = []
    arbol.forEach(s => { ks.push(s.key); s.subs.forEach(sub => ks.push(sub.key)) })
    return ks
  }, [arbol])

  const abierto = (key) => !cerrados.has(key)
  const toggleNodo = (key) => setCerrados(prev => {
    const n = new Set(prev)
    n.has(key) ? n.delete(key) : n.add(key)
    return n
  })
  const expandirTodo = () => setCerrados(new Set())
  const colapsarTodo = () => setCerrados(new Set(todasLasClaves))

  // Estado de un grupo de paths para un rol: { checked (todos), indeterminate (algunos) }
  const estadoGrupo = (rol, paths) => {
    if (paths.length === 0) return { checked: false, indeterminate: false }
    const set = matriz[rol]
    const n = paths.reduce((acc, p) => acc + (set.has(p) ? 1 : 0), 0)
    return { checked: n === paths.length, indeterminate: n > 0 && n < paths.length }
  }

  // Marca/desmarca un conjunto de paths para un rol y guarda.
  const setPaths = async (rol, paths, value) => {
    if (paths.length === 0) return
    setError('')
    const next = new Set(matriz[rol])
    paths.forEach(p => value ? next.add(p) : next.delete(p))
    setMatriz(m => ({ ...m, [rol]: next }))
    setGuardando(rol)
    try {
      await menuPermisosApi.guardar(rol, [...next])
    } catch {
      setError(`No se pudo guardar ${etiquetaRol(rol)}. Reintentá.`)
    } finally {
      setGuardando('')
    }
  }

  const toggleGrupo = (rol, paths) => {
    const { checked } = estadoGrupo(rol, paths)
    setPaths(rol, paths, !checked)
  }

  if (!matriz) return <p style={{ color: '#94a3b8' }}>{error || 'Cargando...'}</p>

  return (
    <div style={{ maxWidth: 920, margin: '0 auto' }}>
      <div style={st.head}>
        <div>
          <h2 style={st.title}>Menú por rol</h2>
          <p style={st.sub}>
            Tildá qué ve cada rol en el menú lateral. Los cambios se guardan solos. El checkbox de una
            sección marca/desmarca todo lo que tiene adentro (queda <b>intermedio</b> si hay solo algunos).
            El <b>Administrador</b> siempre ve todo. Los repartidores entran por el link público.
          </p>
        </div>
        <div style={st.headBtns}>
          <Boton variante="secondary" sm onClick={expandirTodo}>▾ Expandir todo</Boton>
          <Boton variante="secondary" sm onClick={colapsarTodo}>▸ Colapsar todo</Boton>
        </div>
      </div>
      {isSuperAdmin && !impersonateRole && <PermisosPorPersona />}

      {error && <p style={st.error}>{error}</p>}

      <div style={st.tableWrap}>
        <table style={st.table}>
          <thead>
            <tr>
              <th style={{ ...st.th, textAlign: 'left' }}>Sección / Ítem</th>
              {roles.map(r => (
                <th key={r} style={st.th}>
                  {etiquetaRol(r)}{guardando === r && <span style={st.savingDot}> •</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {arbol.map(sec => {
              const secAbierta = abierto(sec.key)
              const vacia = sec.allPaths.length === 0
              return (
                <FragmentRows key={sec.key}>
                  {/* Fila de SECCIÓN */}
                  <tr style={st.trSeccion}>
                    <td style={st.tdSeccion}>
                      <div style={st.secInner}>
                        <button
                          style={{ ...st.caret, visibility: vacia ? 'hidden' : 'visible' }}
                          onClick={() => toggleNodo(sec.key)}
                          aria-label={secAbierta ? 'Colapsar' : 'Expandir'}
                        >{secAbierta ? '▾' : '▸'}</button>
                        <span style={st.secIcon}>{sec.icon}</span>
                        <span style={st.secLabel}>{sec.label}</span>
                        {vacia && <span style={st.vacio}>— sin ítems —</span>}
                      </div>
                    </td>
                    {roles.map(r => {
                      const e = estadoGrupo(r, sec.allPaths)
                      return (
                        <td key={r} style={st.tdCheck}>
                          <TriCheck
                            checked={e.checked}
                            indeterminate={e.indeterminate}
                            disabled={vacia}
                            onChange={() => toggleGrupo(r, sec.allPaths)}
                            title={`${sec.label} — ${etiquetaRol(r)}`}
                          />
                        </td>
                      )
                    })}
                  </tr>

                  {/* Hijos de la sección */}
                  {secAbierta && (
                    <>
                      {/* Ítems directos (hojas) */}
                      {sec.directos.map(it => (
                        <tr key={it.path} style={st.trItem}>
                          <td style={{ ...st.tdItem, paddingLeft: 44 }}>
                            <span style={st.bullet}>·</span>{it.label}
                          </td>
                          {roles.map(r => (
                            <td key={r} style={st.tdCheck}>
                              <input
                                type="checkbox"
                                checked={matriz[r].has(it.path)}
                                onChange={() => setPaths(r, [it.path], !matriz[r].has(it.path))}
                                style={{ width: 17, height: 17, cursor: 'pointer' }}
                              />
                            </td>
                          ))}
                        </tr>
                      ))}

                      {/* LLAVES del sistema: se muestran para que se sepa que
                          existen, pero no se reparten. Quien entra ahí puede
                          darse a sí mismo todo lo demás (Menú por rol, Roles,
                          Usuarios) o leer lo que no le corresponde —Tablas es
                          una consola SQL sobre toda la base, incluidos los
                          sueldos—. Un ítem oculto se busca; uno bloqueado con
                          su motivo se entiende. */}
                      {(sec.llaves || []).map(label => (
                        <tr key={`llave-${label}`} style={st.trItem}>
                          <td style={{ ...st.tdItem, paddingLeft: 44, color: '#94a3b8' }}>
                            <span style={st.bullet}>·</span>{label}
                            <span style={{ marginLeft: 8, fontSize: 11, color: '#94a3b8' }}>
                              🔒 solo administrador
                            </span>
                          </td>
                          {roles.map(r => (
                            <td key={r} style={st.tdCheck}>
                              <span style={{ color: '#cbd5e1', fontSize: 13 }}>—</span>
                            </td>
                          ))}
                        </tr>
                      ))}

                      {/* Subsecciones */}
                      {sec.subs.map(sub => {
                        const subAbierta = abierto(sub.key)
                        const subPaths = sub.items.map(i => i.path)
                        return (
                          <FragmentRows key={sub.key}>
                            <tr style={st.trSub}>
                              <td style={{ ...st.tdSub, paddingLeft: 32 }}>
                                <button
                                  style={st.caret}
                                  onClick={() => toggleNodo(sub.key)}
                                  aria-label={subAbierta ? 'Colapsar' : 'Expandir'}
                                >{subAbierta ? '▾' : '▸'}</button>
                                <span style={st.subLabel}>{sub.label}</span>
                              </td>
                              {roles.map(r => {
                                const e = estadoGrupo(r, subPaths)
                                return (
                                  <td key={r} style={st.tdCheck}>
                                    <TriCheck
                                      checked={e.checked}
                                      indeterminate={e.indeterminate}
                                      onChange={() => toggleGrupo(r, subPaths)}
                                      title={`${sub.label} — ${etiquetaRol(r)}`}
                                    />
                                  </td>
                                )
                              })}
                            </tr>
                            {subAbierta && sub.items.map(it => (
                              <tr key={it.path} style={st.trItem}>
                                <td style={{ ...st.tdItem, paddingLeft: 64 }}>
                                  <span style={st.bullet}>·</span>{it.label}
                                </td>
                                {roles.map(r => (
                                  <td key={r} style={st.tdCheck}>
                                    <input
                                      type="checkbox"
                                      checked={matriz[r].has(it.path)}
                                      onChange={() => setPaths(r, [it.path], !matriz[r].has(it.path))}
                                      style={{ width: 17, height: 17, cursor: 'pointer' }}
                                    />
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </FragmentRows>
                        )
                      })}
                    </>
                  )}
                </FragmentRows>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// Permite agrupar varias <tr> sin romper el <tbody> (Fragment con key).
function FragmentRows({ children }) {
  return <>{children}</>
}

const st = {
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' },
  title: { margin: '0 0 0.25rem', fontSize: '1.4rem', fontWeight: 700, color: '#1e293b' },
  sub: { margin: '0 0 1.25rem', color: '#64748b', fontSize: '0.9rem', lineHeight: 1.5, maxWidth: 620 },
  headBtns: { display: 'flex', gap: 8, flexShrink: 0 },
  error: { color: '#dc2626', fontSize: '0.875rem' },
  tableWrap: { background: 'white', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' },
  th: { padding: '0.7rem 0.5rem', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontWeight: 700, fontSize: '0.78rem', color: '#475569', textAlign: 'center', whiteSpace: 'nowrap' },

  trSeccion: { background: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
  tdSeccion: { padding: '0.55rem 0.75rem' },
  secInner: { display: 'flex', alignItems: 'center', gap: 6 },
  secIcon: { fontSize: '0.95rem' },
  secLabel: { fontSize: '0.82rem', fontWeight: 800, color: '#334155', letterSpacing: '0.03em' },
  vacio: { fontSize: '0.72rem', color: '#94a3b8', fontStyle: 'italic', marginLeft: 6 },

  trSub: { borderBottom: '1px solid #f1f5f9', background: '#fcfdfe' },
  tdSub: { padding: '0.4rem 0.75rem' },
  subLabel: { fontSize: '0.78rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' },

  trItem: { borderBottom: '1px solid #f1f5f9' },
  tdItem: { padding: '0.4rem 0.75rem', color: '#1e293b' },
  bullet: { color: '#cbd5e1', marginRight: 8, fontWeight: 800 },

  tdCheck: { textAlign: 'center', padding: '0.4rem', width: 110 },
  caret: { background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '0.7rem', padding: 0, width: 14, lineHeight: 1 },
  savingDot: { color: '#16a34a', fontWeight: 800 },
}
